/**
 * Projekt-Liste für die linke Sidebar (Phase-1-Restructure, 2026-06-20).
 *
 * Ersetzt die alten Sidebar-Items „Meine Projekte" + „SF-Projekte" durch
 * direkt klickbare Projekt-Zeilen. Klick aufs Projekt → /projects/{id}.
 *
 * 2026-07-29: Die Chats hängen NICHT mehr als Unterbaum an den Projekten.
 * Gründe (Messwerte in docs/navigation-symbolschiene-konzept.md §1):
 *   · sie waren dort 12px bei 80% Deckkraft, zweifach eingerückt und
 *     abgeschnitten — schlecht lesbar,
 *   · sie zeigten ohnehin nur 5 von N,
 *   · und sie machten die Länge der App-Navigation von einer beliebigen
 *     Datenmenge abhängig (18 Projekte → „Studio" 3775px unter der Faltkante).
 * Sie leben jetzt vollständig auf der Projektseite (Tab „Chats", der für
 * lokale Projekte ohnehin der Standard-Tab ist). Mit dem Unterbaum entfielen
 * auch der Aufklapp-Pfeil (dessen einziger Zweck er war), die Fünfer-Kappung
 * und der „+N weitere Chats"-Link.
 *
 * Bewusst klein gehalten: keine Drag-Sortierung, keine Sub-Items für
 * Knowledge/Tasks/Präsentationen.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import { FolderKanban, Plus, Pencil, X } from "lucide-react";
import { api } from "../api";
import { Button, projectDisplayName } from "../ui";
import { useToast } from "../ui/toast";
import { useT } from "../i18n";

interface ProjectRow {
  id: string;
  name: string;
  owner: string;
}

export default function ProjectsSidebarTree() {
  const toast = useToast();
  const t = useT();
  const pt = t.shared.projekteBaum;
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  // Rename-State analog Chat.tsx: id + mode (inline-sidebar vs mobile-modal).
  const [renameTarget, setRenameTarget] = useState<
    { id: string; mode: "inline" | "modal" } | null
  >(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  const startRename = (id: string, name: string, mode: "inline" | "modal") => {
    setRenameTarget({ id, mode });
    setRenameValue(name);
  };
  const cancelRename = () => {
    setRenameTarget(null);
    setRenameValue("");
  };
  const commitRename = async () => {
    if (!renameTarget) return;
    const t = renameValue.trim();
    if (!t) {
      cancelRename();
      return;
    }
    setRenameBusy(true);
    try {
      const updated = await api.patch<ProjectRow>(`/projects/${renameTarget.id}`, { name: t });
      setProjects((prev) =>
        prev.map((p) => (p.id === updated.id ? { ...p, name: updated.name } : p))
      );
      cancelRename();
    } catch (e) {
      toast.err(pt.umbenennenFehlgeschlagen, (e as Error).message);
    } finally {
      setRenameBusy(false);
    }
  };
  const location = useLocation();
  const navigate = useNavigate();
  // Click-Delay-Ref: single-click navigiert nach 250ms wenn kein 2nd Click kam.
  // Double-click cancelt das Timer + startet inline-rename.
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Nur noch die Projekte. Der zweite Aufruf (`/chats?limit=200`) ist mit dem
  // Chat-Unterbaum entfallen — die Chat-Liste laedt jetzt die Projektseite,
  // und zwar nur die des offenen Projekts.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ps = await api.get<ProjectRow[]>("/projects");
        if (cancelled) return;
        setProjects(ps);
      } catch {
        /* silent — Layout zeigt nichts statt Crash */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Projekt-Loeschung aus der Projektseite spiegeln: der Tree laedt Projekte
  // nur einmal beim Mount, ohne diesen Sync bliebe ein geloeschtes Projekt hier
  // klickbar (→ 404). Event wird in Projects.tsx nach erfolgreichem DELETE (204)
  // gefeuert.
  useEffect(() => {
    const onDeleted = (e: Event) => {
      const id = (e as CustomEvent<{ id: string }>).detail?.id;
      if (!id) return;
      setProjects((prev) => prev.filter((p) => p.id !== id));
    };
    window.addEventListener("sb:project-deleted", onDeleted);
    return () => window.removeEventListener("sb:project-deleted", onDeleted);
  }, []);

  if (projects.length === 0) {
    return (
      <div data-projekte-leer className="px-2 py-2 text-[11px] text-ink-50/50">
        {pt.leer}
      </div>
    );
  }

  return (
    <div data-projekte-baum className="space-y-0.5">
      {projects.map((p) => {
        const onProjectPath =
          location.pathname === `/projects/${p.id}` ||
          location.pathname.startsWith(`/projects/${p.id}/`);
        return (
          <div key={p.id}>
            <div
              data-project-row={p.id}
              className={clsx(
                "group flex items-center gap-0.5 rounded-md text-sm transition-colors pl-1.5",
                onProjectPath
                  ? "bg-brand-50 dark:bg-ink-700 text-brand-700 dark:text-accent-300"
                  : "text-ink-50 hover:bg-cream-100 dark:hover:bg-ink-700/60"
              )}
            >
              {renameTarget?.id === p.id && renameTarget.mode === "inline" ? (
                <div className="flex-1 min-w-0 flex items-center gap-1.5 min-h-11 md:min-h-0 md:py-1 pr-2">
                  <FolderKanban className="w-3.5 h-3.5 shrink-0 text-brand-500" />
                  <input
                    type="text"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); commitRename(); }
                      if (e.key === "Escape") { e.preventDefault(); cancelRename(); }
                    }}
                    autoFocus
                    disabled={renameBusy}
                    aria-label={pt.umbenennen}
                    maxLength={200}
                    className="flex-1 min-w-0 px-1 py-0.5 rounded-md border border-brand-500 bg-surface dark:bg-ink-900 text-sm"
                  />
                </div>
              ) : (
                <a
                  href={`/projects/${p.id}`}
                  className="flex-1 min-w-0 flex items-center gap-1.5 min-h-11 md:min-h-0 md:py-1 pr-2"
                  onClick={(e) => {
                    // Modifier/Middle-Click: native handling (open in new tab).
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                    e.preventDefault();
                    if (clickTimerRef.current) {
                      clearTimeout(clickTimerRef.current);
                      clickTimerRef.current = null;
                    }
                    if (e.detail >= 2) {
                      // Double-click: enter inline-rename
                      startRename(p.id, p.name, "inline");
                      return;
                    }
                    // Single-click: delay navigation so second click can cancel
                    clickTimerRef.current = setTimeout(() => {
                      clickTimerRef.current = null;
                      navigate(`/projects/${p.id}`);
                    }, 230);
                  }}
                  title={pt.zeileTitel}
                >
                  <FolderKanban
                    className={clsx(
                      "w-3.5 h-3.5 shrink-0",
                      onProjectPath ? "text-brand-500" : "text-ink-50/50"
                    )}
                  />
                  <span className="truncate">{projectDisplayName(p.name)}</span>
                </a>
              )}
              {renameTarget?.id !== p.id && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    startRename(p.id, p.name, "modal");
                  }}
                  className="md:hidden text-ink-50/40 hover:text-brand-600 shrink-0 flex items-center justify-center min-h-11 min-w-11"
                  title={pt.umbenennen}
                  aria-label={pt.umbenennen}
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        );
      })}
      <Link
        to="/p"
        className="mt-2 flex items-center gap-1.5 px-2 py-1 rounded-md text-[12px] text-ink-50/70 hover:bg-cream-100 dark:hover:bg-ink-700/60"
      >
        <Plus className="w-3.5 h-3.5 shrink-0" />
        <span>{pt.neuesProjekt}</span>
      </Link>

      {/* Mobile-Rename-Modal — Pencil-Icon-Trigger.
          Desktop nutzt Inline-Input im Sidebar-Item via onDoubleClick. */}
      <Dialog.Root
        open={renameTarget?.mode === "modal"}
        onOpenChange={(o) => { if (!o) cancelRename(); }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-fade-in" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-surface border border-border rounded-2xl shadow-2xl w-[420px] max-w-[92vw] p-5 animate-modal-in">
            <div className="flex items-center justify-between mb-3">
              <Dialog.Title className="font-semibold text-sm">{pt.umbenennen}</Dialog.Title>
              <button
                onClick={cancelRename}
                aria-label={pt.schliessen}
                className="text-text-3 hover:text-text min-h-11 min-w-11 md:min-h-0 md:min-w-0 flex items-center justify-center"
              >
                <X className="w-5 h-5 md:w-4 md:h-4" />
              </button>
            </div>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); commitRename(); }
                if (e.key === "Escape") { e.preventDefault(); cancelRename(); }
              }}
              autoFocus
              disabled={renameBusy}
              aria-label={pt.nameFeldAria}
              maxLength={200}
              className="w-full px-3 py-2 rounded-md border border-border-strong bg-surface dark:bg-ink-900 text-sm min-h-11 md:min-h-0"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="secondary"
                onClick={cancelRename}
                disabled={renameBusy}
                className="min-h-11 md:min-h-0"
              >
                {t.common.abbrechen}
              </Button>
              <Button
                variant="primary"
                onClick={commitRename}
                loading={renameBusy}
                disabled={!renameValue.trim()}
                className="min-h-11 md:min-h-0"
              >
                {t.common.speichern}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
